import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SITE_URL, siteUrl, DEFAULT_OG_IMAGE, SITE_META, formatSeoTitle } from '../lib/site';

function buildDefaultJsonLd(canonical, seoTitle, seoDescription) {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'InnovHer',
      alternateName: ['Innovher', 'Innov Her'],
      url: SITE_URL,
      logo: DEFAULT_OG_IMAGE,
      image: DEFAULT_OG_IMAGE,
      description: SITE_META.organizationDescription,
      email: 'info@innovher.com',
      telephone: '+91-78915-02909',
      foundingLocation: {
        '@type': 'Place',
        name: 'Jaipur, Rajasthan, India',
      },
      sameAs: [
        'https://www.linkedin.com/company/innovher.com/',
        'https://www.instagram.com/innovher/',
        'https://www.facebook.com/profile.php?id=61551582736876',
        'https://twitter.com/innovher',
      ],
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Jaipur',
        addressRegion: 'Rajasthan',
        addressCountry: 'IN',
      },
      areaServed: {
        '@type': 'Country',
        name: 'India',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'InnovHer',
      url: SITE_URL,
      description: SITE_META.defaultDescription,
      publisher: {
        '@type': 'Organization',
        name: 'InnovHer',
        url: SITE_URL,
      },
      inLanguage: 'en-IN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: seoTitle,
      description: seoDescription,
      url: canonical,
      isPartOf: {
        '@type': 'WebSite',
        name: 'InnovHer',
        url: SITE_URL,
      },
      about: {
        '@type': 'Organization',
        name: 'InnovHer',
      },
    },
  ];
}

export default function SEO({ title, description, keywords, name, type, path, jsonLd, image, noIndex }) {
  const location = useLocation();

  const seoTitle = formatSeoTitle(title);
  const seoDescription = description || SITE_META.defaultDescription;
  const seoKeywords = keywords || SITE_META.defaultKeywords;
  const pagePath = path != null ? path : location?.pathname || '/';
  const canonical = siteUrl(pagePath);
  const ogImage = image || DEFAULT_OG_IMAGE;
  const isPrivate =
    noIndex ||
    pagePath.startsWith('/admin') ||
    pagePath.startsWith('/founder') ||
    pagePath.startsWith('/mentor-dashboard') ||
    pagePath.startsWith('/investor-dashboard') ||
    pagePath.startsWith('/login') ||
    pagePath.startsWith('/forgot-password') ||
    pagePath.startsWith('/reset-password') ||
    pagePath.startsWith('/notifications');

  const schema = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : buildDefaultJsonLd(canonical, seoTitle, seoDescription);

  return (
    <Helmet>
      <html lang="en" />
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content="InnovHer" />
      <meta name="publisher" content="InnovHer" />
      <meta name="application-name" content="InnovHer" />
      <meta name="theme-color" content="#08080a" />
      <meta name="color-scheme" content="dark" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="referrer" content="strict-origin-when-cross-origin" />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={isPrivate ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'} />
      <meta name="googlebot" content={isPrivate ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="bingbot" content={isPrivate ? 'noindex, nofollow' : 'index, follow'} />

      {/* Geo / locale */}
      <meta name="geo.region" content="IN-RJ" />
      <meta name="geo.placename" content="Jaipur" />
      <meta name="language" content="English" />

      {/* Open Graph */}
      <meta property="og:type" content={type || 'website'} />
      <meta property="og:site_name" content="InnovHer" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:alt" content="InnovHer" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@innovher" />
      <meta name="twitter:creator" content={name || '@innovher'} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="InnovHer" />

      {/* Apple / mobile */}
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="InnovHer" />
      <meta name="mobile-web-app-capable" content="yes" />

      {schema.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
